// File: frontend/src/services/masterDataService.ts
//
// Client for the master data API.
// Public endpoints are used by frontend dropdowns (leagues, rounds, countries).
// Admin endpoints are used by the AdminMasterData management panel.

import { apiClient } from "./apiClient";
import { API_BASE_URL } from "../constants";

// ── DTOs (mirror backend) ─────────────────────────────────────────────────────

export interface MdCountryDto {
  id: number;
  code: string;
  displayName: string;
  flagEmoji: string | null;
  displayOrder: number;
  active: boolean;
}

export interface MdLeagueDto {
  id: number;
  name: string;
  sportType: string;
  countryCode: string;
  countryFlag: string | null;
  priority: boolean;
  displayOrder: number;
  active: boolean;
}

export interface MdRoundDto {
  id: number;
  name: string;
  sportType: string | null;
  displayOrder: number;
  active: boolean;
}

export interface MdTopicCategoryDto {
  id: number;
  name: string;
  displayOrder: number;
  active: boolean;
  // Optional react-icons key (e.g. "FaFilm") — null/undefined when no icon is assigned
  iconKey?: string | null;
  // "ISTIKRAM" / "BIRAK" for the two reserved system channels that back the
  // İstikram and Bırak domain tabs, null/undefined for every normal channel.
  // Admin UI uses this to hide delete/deactivate and lock the name field.
  systemKey?: string | null;
}

// ── Save request shapes ───────────────────────────────────────────────────────

export interface SaveMdCountryRequest {
  code: string;
  displayName: string;
  flagEmoji?: string;
  displayOrder: number;
}

export interface SaveMdLeagueRequest {
  name: string;
  sportType: string;
  countryCode: string;
  countryFlag?: string;
  priority: boolean;
  displayOrder: number;
}

export interface SaveMdRoundRequest {
  name: string;
  sportType?: string | null;
  displayOrder: number;
}

export interface SaveMdTopicCategoryRequest {
  name: string;
  // IGNORED on create — new channels are always slotted into their
  // alphabetical position by the backend. Respected on update, where admins
  // can still move a channel manually.
  displayOrder?: number;
  // Optional react-icons key — omit or send empty string to clear the icon
  iconKey?: string | null;
}

// ── Public read endpoints (no auth) ──────────────────────────────────────────

/** Active topic categories ("konu") for the suggest/create topic dropdowns. */
export const getPublicTopicCategories = (): Promise<MdTopicCategoryDto[]> =>
  apiClient.get<MdTopicCategoryDto[]>("/api/public/masterdata/topic-categories");

/** Active countries for dropdown use. */
export const getPublicCountries = (): Promise<MdCountryDto[]> =>
  apiClient.get<MdCountryDto[]>("/api/public/masterdata/countries");

/** Active leagues for a sport type. */
export const getPublicLeagues = (sport: string): Promise<MdLeagueDto[]> =>
  apiClient.get<MdLeagueDto[]>(`/api/public/masterdata/leagues?${new URLSearchParams({ sport })}`);

/** Active rounds for a sport type (plus universal rounds). */
export const getPublicRounds = (sport: string): Promise<MdRoundDto[]> =>
  apiClient.get<MdRoundDto[]>(`/api/public/masterdata/rounds?${new URLSearchParams({ sport })}`);

// ── Admin read endpoints ──────────────────────────────────────────────────────

/** All countries including inactive — for admin list. */
export const adminGetCountries = (): Promise<MdCountryDto[]> =>
  apiClient.get<MdCountryDto[]>("/api/admin/masterdata/countries");

/** All leagues (optionally filtered by sport) including inactive — for admin list. */
export const adminGetLeagues = (sport?: string): Promise<MdLeagueDto[]> => {
  const qs = sport ? `?${new URLSearchParams({ sport })}` : "";
  return apiClient.get<MdLeagueDto[]>(`/api/admin/masterdata/leagues${qs}`);
};

/** All rounds including inactive — for admin list. */
export const adminGetRounds = (): Promise<MdRoundDto[]> =>
  apiClient.get<MdRoundDto[]>("/api/admin/masterdata/rounds");

// ── Admin write endpoints ─────────────────────────────────────────────────────

export const adminCreateCountry = (body: SaveMdCountryRequest): Promise<MdCountryDto> =>
  apiClient.post<MdCountryDto>("/api/admin/masterdata/countries", body);

export const adminUpdateCountry = (id: number, body: SaveMdCountryRequest): Promise<MdCountryDto> =>
  apiClient.put<MdCountryDto>(`/api/admin/masterdata/countries/${id}`, body);

export const adminToggleCountry = (id: number): Promise<MdCountryDto> =>
  apiClient.patch<MdCountryDto>(`/api/admin/masterdata/countries/${id}/toggle`);

export const adminCreateLeague = (body: SaveMdLeagueRequest): Promise<MdLeagueDto> =>
  apiClient.post<MdLeagueDto>("/api/admin/masterdata/leagues", body);

export const adminUpdateLeague = (id: number, body: SaveMdLeagueRequest): Promise<MdLeagueDto> =>
  apiClient.put<MdLeagueDto>(`/api/admin/masterdata/leagues/${id}`, body);

export const adminToggleLeague = (id: number): Promise<MdLeagueDto> =>
  apiClient.patch<MdLeagueDto>(`/api/admin/masterdata/leagues/${id}/toggle`);

export const adminCreateRound = (body: SaveMdRoundRequest): Promise<MdRoundDto> =>
  apiClient.post<MdRoundDto>("/api/admin/masterdata/rounds", body);

export const adminUpdateRound = (id: number, body: SaveMdRoundRequest): Promise<MdRoundDto> =>
  apiClient.put<MdRoundDto>(`/api/admin/masterdata/rounds/${id}`, body);

export const adminToggleRound = (id: number): Promise<MdRoundDto> =>
  apiClient.patch<MdRoundDto>(`/api/admin/masterdata/rounds/${id}/toggle`);

export const adminGetTopicCategories = (): Promise<MdTopicCategoryDto[]> =>
  apiClient.get<MdTopicCategoryDto[]>("/api/admin/masterdata/topic-categories");

export const adminCreateTopicCategory = (body: SaveMdTopicCategoryRequest): Promise<MdTopicCategoryDto> =>
  apiClient.post<MdTopicCategoryDto>("/api/admin/masterdata/topic-categories", body);

export const adminUpdateTopicCategory = (id: number, body: SaveMdTopicCategoryRequest): Promise<MdTopicCategoryDto> =>
  apiClient.put<MdTopicCategoryDto>(`/api/admin/masterdata/topic-categories/${id}`, body);

export const adminToggleTopicCategory = (id: number): Promise<MdTopicCategoryDto> =>
  apiClient.patch<MdTopicCategoryDto>(`/api/admin/masterdata/topic-categories/${id}/toggle`);

/** Number of custom topics still attached to the channel — for the delete confirmation. */
export const adminGetTopicCategoryTopicCount = (id: number): Promise<{ count: number }> =>
  apiClient.get<{ count: number }>(`/api/admin/masterdata/topic-categories/${id}/topic-count`);

/** Hard-deletes a channel; its topics become channel-less. Nickaltı channel is protected. */
export const adminDeleteTopicCategory = (id: number): Promise<void> =>
  apiClient.delete<void>(`/api/admin/masterdata/topic-categories/${id}`);
// ── MdPersonRole ─────────────────────────────────────────────────────────────

export interface MdPersonRoleDto {
  id: number;
  code: string;
  nameTr: string;
  nameEn: string;
  iconKey: string;
  category: string;
  /**
   * What a career entry with this role is attached to:
   *  - "CLUB"         : a club record; club search is offered and a club is required
   *  - "ORGANIZATION" : a non-club employer (TV channel, newspaper, federation) as free text
   *  - "NONE"         : no affiliation at all (referees)
   * Older rows default to "CLUB" server-side.
   */
  affiliationType: string;
  sortOrder: number;
  active: boolean;
}

export interface SaveMdPersonRoleRequest {
  code: string;
  nameTr: string;
  nameEn: string;
  iconKey: string;
  category: string;
  // CLUB | ORGANIZATION | NONE — see MdPersonRoleDto.affiliationType
  affiliationType: string;
  sortOrder: number;
}

/** Active roles for dropdown — public, no auth required. */
export const getPublicPersonRoles = (): Promise<MdPersonRoleDto[]> =>
  apiClient.get<MdPersonRoleDto[]>("/api/public/masterdata/person-roles");

/** All roles including inactive — admin only. */
export const adminGetPersonRoles = (): Promise<MdPersonRoleDto[]> =>
  apiClient.get<MdPersonRoleDto[]>("/api/admin/masterdata/person-roles");

export const adminCreatePersonRole = (body: SaveMdPersonRoleRequest): Promise<MdPersonRoleDto> =>
  apiClient.post<MdPersonRoleDto>("/api/admin/masterdata/person-roles", body);

export const adminUpdatePersonRole = (id: number, body: SaveMdPersonRoleRequest): Promise<MdPersonRoleDto> =>
  apiClient.put<MdPersonRoleDto>(`/api/admin/masterdata/person-roles/${id}`, body);

export const adminTogglePersonRole = (id: number): Promise<MdPersonRoleDto> =>
  apiClient.patch<MdPersonRoleDto>(`/api/admin/masterdata/person-roles/${id}/toggle`);

/** Renames all roles in oldCategory to newCategory in one batch. */
export const adminRenamePersonRoleCategory = async (oldCategory: string, newCategory: string): Promise<void> => {
  const token = localStorage.getItem("token");
  const response = await fetch(`${API_BASE_URL}/api/admin/masterdata/person-roles/rename-category`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ oldCategory, newCategory }),
  });
  if (!response.ok) {
    throw new Error(`Rename failed: ${response.status}`);
  }
  // Intentionally ignore body — endpoint returns 200/204 with no content
};